// The column options sheet of the phone board, opened by the column's "..."
// button. Every row calls the handler the desktop column header already uses;
// only the controls are new.
//
// Rows, in the approved order: Category, Cards, Show, How this column works,
// Reset, Share. Two rows are additions, because the desktop header has the
// control and a phone reader would otherwise lose it: Filters (the popover with
// the category, tag and threshold filters) and, for the one column whose schema
// has it, Scope.

import { useEffect, useState, type ReactNode } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { getMetric } from '../api/metrics';
import { fieldSpec } from '../api/useSchema';
import type {
    ActivityScope,
    BoardFilters,
    BoardSchema,
} from '../api/boardTypes';
import type { BodyMode, CardView, ColumnConfig } from '../board/columnConfig';
import type { CategoryConfig } from '../board/categories';
import { CategoryMenu } from '../board/CategoryMenu';
import { ColumnFilters } from '../board/ColumnFilters';
import { ColumnInfoPanel } from '../board/ColumnInfoPanel';
import { LocalSheet } from './LocalSheet';
import {
    ChevronDownIcon,
    ChevronRightIcon,
    FiltersIcon,
    InfoIcon,
    ResetIcon,
    ShareIcon,
} from './icons';
import { ink } from './surface';

export interface ColumnOptionsSheetProps {
    open: boolean;
    onClose: () => void;
    config: ColumnConfig;
    schema: BoardSchema;
    filters: BoardFilters;
    bodyMode: BodyMode;
    cardView: CardView;
    filtersDirty: boolean;
    now: number;
    onFiltersChange: (patch: Partial<BoardFilters>) => void;
    onReset: () => void;
    onShare: () => string;
    onBodyModeChange: (mode: BodyMode) => void;
    onCardViewChange: (view: CardView) => void;
    onCategoryChange: (category: CategoryConfig) => void;
    onFocusEvent?: (eventId: string) => void;
}

const SCOPE_LABELS: Record<string, string> = {
    market: 'Market',
    event: 'Event',
};

const SCOPE_HINTS: Record<string, string> = {
    market: 'Rank each market leg on its own',
    event: 'Fold the legs into one number per event',
};

const COPIED_MS = 2000;

const hairline = (theme: Theme) => `1px solid ${ink(theme, 0.08)}`;

const rowSx = (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box' as const,
    px: '20px',
    borderTop: hairline(theme),
    fontFamily: 'inherit',
    fontSize: 16,
    color: theme.palette.text.primary,
    textAlign: 'left' as const,
});

function Segmented<T extends string>({
    label,
    value,
    options,
    onChange,
}: {
    label: string;
    value: T;
    options: { value: T; label: string }[];
    onChange: (value: T) => void;
}) {
    return (
        <Box
            role="group"
            aria-label={label}
            sx={theme => ({
                display: 'grid',
                gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
                // 180 px of buttons inside the 1 px border, as drawn.
                width: 182,
                flexShrink: 0,
                border: `1px solid ${ink(theme, 0.2)}`,
                borderRadius: '8px',
                overflow: 'hidden',
            })}
        >
            {options.map((option, index) => {
                const pressed = option.value === value;
                return (
                    <ButtonBase
                        key={option.value}
                        aria-pressed={pressed}
                        onClick={() => onChange(option.value)}
                        sx={theme => ({
                            height: 44,
                            fontFamily: 'inherit',
                            fontSize: 14,
                            fontWeight: pressed ? 500 : 400,
                            color: pressed
                                ? theme.palette.primary.main
                                : ink(theme, 0.75),
                            bgcolor: pressed
                                ? alpha(theme.palette.primary.main, 0.12)
                                : 'transparent',
                            borderLeft:
                                index > 0
                                    ? `1px solid ${ink(theme, 0.2)}`
                                    : 'none',
                        })}
                    >
                        {option.label}
                    </ButtonBase>
                );
            })}
        </Box>
    );
}

function OptionRow({
    label,
    caption,
    minHeight = 57,
    children,
}: {
    label: string;
    caption?: string;
    minHeight?: number;
    children: ReactNode;
}) {
    return (
        <Box
            sx={theme => ({
                ...rowSx(theme),
                justifyContent: 'space-between',
                gap: 2,
                minHeight,
            })}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 16, lineHeight: 'normal' }}>
                    {label}
                </Typography>
                {caption && (
                    <Typography
                        sx={theme => ({
                            fontSize: 12,
                            lineHeight: 'normal',
                            color: ink(theme, 0.6),
                        })}
                    >
                        {caption}
                    </Typography>
                )}
            </Box>
            {children}
        </Box>
    );
}

function ActionRow({
    icon,
    label,
    trailing,
    minHeight = 53,
    onClick,
}: {
    icon: ReactNode;
    label: string;
    trailing?: ReactNode;
    // 53 is the mockup's 52 px plus the 1 px rule of a link row; its button
    // rows (reset, share) hold the rule inside the 52.
    minHeight?: number;
    onClick: () => void;
}) {
    return (
        <ButtonBase
            onClick={onClick}
            sx={theme => ({
                ...rowSx(theme),
                gap: '14px',
                minHeight,
            })}
        >
            <Box
                component="span"
                sx={theme => ({ display: 'flex', color: ink(theme, 0.6) })}
            >
                {icon}
            </Box>
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                {label}
            </Box>
            {trailing}
        </ButtonBase>
    );
}

export function ColumnOptionsSheet({
    open,
    onClose,
    config,
    schema,
    filters,
    bodyMode,
    cardView,
    filtersDirty,
    now,
    onFiltersChange,
    onReset,
    onShare,
    onBodyModeChange,
    onCardViewChange,
    onCategoryChange,
    onFocusEvent,
}: ColumnOptionsSheetProps) {
    const [categoryAnchor, setCategoryAnchor] = useState<HTMLElement | null>(
        null
    );
    const [infoOpen, setInfoOpen] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    // A sheet that was closed under its sub-sheet must not reopen on it.
    useEffect(() => {
        if (open) return;
        setInfoOpen(false);
        setFiltersOpen(false);
    }, [open]);

    useEffect(() => {
        if (!copied) return;
        const timer = setTimeout(() => setCopied(false), COPIED_MS);
        return () => clearTimeout(timer);
    }, [copied]);

    const scopeSpec = fieldSpec(schema, config.source, 'scope');
    const scope = filters.scope ?? scopeSpec?.default ?? 'market';

    const share = async () => {
        const url = onShare();
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
        } catch {
            window.prompt('Copy this link', url);
        }
    };

    const sortedBy = getMetric(filters.sortBy).label.toLowerCase();
    const subtitle = `Sorted by ${sortedBy}, ${
        filters.sortDir === 'desc' ? 'highest' : 'lowest'
    } first`;

    return (
        <>
            <LocalSheet
                open={open}
                onClose={onClose}
                title={`${config.title} column`}
                subtitle={subtitle}
                label={`${config.title} column options`}
            >
                <OptionRow label="Category" minHeight={53}>
                    <ButtonBase
                        aria-haspopup="menu"
                        aria-label={`Category: ${config.title}`}
                        onClick={event =>
                            setCategoryAnchor(event.currentTarget)
                        }
                        sx={theme => ({
                            height: 44,
                            gap: '4px',
                            pl: '12px',
                            pr: '4px',
                            minWidth: 0,
                            fontFamily: 'inherit',
                            fontSize: 16,
                            color: theme.palette.primary.main,
                        })}
                    >
                        <Box
                            component="span"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {config.title}
                        </Box>
                        <ChevronDownIcon size={20} />
                    </ButtonBase>
                </OptionRow>

                <OptionRow label="Cards">
                    <Segmented<CardView>
                        label="Card size"
                        value={cardView}
                        options={[
                            { value: 'card', label: 'Full' },
                            { value: 'row', label: 'Compact' },
                        ]}
                        onChange={onCardViewChange}
                    />
                </OptionRow>

                <OptionRow label="Show">
                    <Segmented<BodyMode>
                        label="Card body"
                        value={bodyMode}
                        options={[
                            { value: 'view', label: 'Values' },
                            { value: 'trend', label: 'Trend' },
                        ]}
                        onChange={onBodyModeChange}
                    />
                </OptionRow>

                {scopeSpec && (
                    <OptionRow label="Scope" caption={SCOPE_HINTS[scope]}>
                        <Segmented<ActivityScope>
                            label="Scope"
                            value={scope as ActivityScope}
                            options={(scopeSpec.options ?? []).map(option => ({
                                value: option as ActivityScope,
                                label: SCOPE_LABELS[option] ?? option,
                            }))}
                            onChange={value =>
                                onFiltersChange({ scope: value })
                            }
                        />
                    </OptionRow>
                )}

                <ActionRow
                    icon={<FiltersIcon />}
                    label="Filters"
                    onClick={() => setFiltersOpen(true)}
                    trailing={
                        <>
                            {filtersDirty && (
                                <Box
                                    component="span"
                                    role="img"
                                    aria-label="changed"
                                    sx={theme => ({
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: theme.palette.primary.main,
                                    })}
                                />
                            )}
                            <Box
                                component="span"
                                sx={theme => ({
                                    display: 'flex',
                                    color: ink(theme, 0.45),
                                })}
                            >
                                <ChevronRightIcon />
                            </Box>
                        </>
                    }
                />

                <ActionRow
                    icon={<InfoIcon />}
                    label="How this column works"
                    onClick={() => setInfoOpen(true)}
                    trailing={
                        <Box
                            component="span"
                            sx={theme => ({
                                display: 'flex',
                                color: ink(theme, 0.45),
                            })}
                        >
                            <ChevronRightIcon />
                        </Box>
                    }
                />

                <ActionRow
                    icon={<ResetIcon />}
                    label="Reset this column"
                    minHeight={52}
                    onClick={() => {
                        onReset();
                        onClose();
                    }}
                />

                <ActionRow
                    icon={<ShareIcon />}
                    label={copied ? 'Link copied' : 'Share this column'}
                    minHeight={52}
                    onClick={share}
                />
            </LocalSheet>

            <CategoryMenu
                touch
                anchorEl={categoryAnchor}
                schema={schema}
                current={{ source: config.source, group: config.group }}
                onPick={category => {
                    setCategoryAnchor(null);
                    onCategoryChange(category);
                    onClose();
                }}
                onClose={() => setCategoryAnchor(null)}
            />

            <LocalSheet
                open={open && infoOpen}
                onClose={() => setInfoOpen(false)}
                title="How this column works"
                nested
            >
                <ColumnInfoPanel
                    touch
                    width="100%"
                    schema={schema}
                    column={config.source}
                    title={config.group !== null ? config.title : undefined}
                    filters={filters}
                    now={now}
                    onFiltersChange={onFiltersChange}
                    onFocusEvent={eventId => {
                        onFocusEvent?.(eventId);
                        setInfoOpen(false);
                        onClose();
                    }}
                />
            </LocalSheet>

            <LocalSheet
                open={open && filtersOpen}
                onClose={() => setFiltersOpen(false)}
                title="Filters"
                label={`${config.title} column filters`}
                nested
            >
                <ColumnFilters
                    touch
                    schema={schema}
                    column={config.source}
                    effective={filters}
                    onChange={onFiltersChange}
                    onReset={onReset}
                    onShare={onShare}
                />
            </LocalSheet>
        </>
    );
}
